import { io } from "socket.io-client";
import { URL } from "../Component/AxiosUrlGlobal.js";

/**
 * Socket.io Client for Admin Real-time Updates
 * Manages WebSocket connection for live analytics updates
 */

const SOCKET_URL = URL; // Imported from AxiosUrlGlobal.js

class SocketClient {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
  }

  /**
   * Connect to Socket.io server
   */
  connect() {
    if (this.socket?.connected) {
      console.log("Socket already connected");
      return;
    }

    const userId = localStorage.getItem("id");
    const token = localStorage.getItem("token");

    if (!userId || !token) {
      console.error("No user ID or token found for socket connection");
      return;
    }

    this.socket = io(SOCKET_URL, {
      query: {
        uid: userId,
        token: token,
      },
      transports: ["websocket", "polling"],
    });

    this.socket.on("connect", () => {
      console.log("Socket connected:", this.socket.id);
      this.subscribeToAnalytics();
    });

    this.socket.on("disconnect", (reason) => {
      console.log("Socket disconnected:", reason);
    });

    this.socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
    });

    return this.socket;
  }

  /**
   * Disconnect from Socket.io server
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.listeners.clear();
    }
  }

  /**
   * Subscribe to analytics updates
   */
  subscribeToAnalytics() {
    if (this.socket?.connected) {
      this.socket.emit("admin:subscribe");
    }
  }

  /**
   * Listen for online users count updates
   * @param {Function} callback - Called with { count: number }
   */
  onOnlineUsersUpdate(callback) {
    if (!this.socket) return;

    const eventName = "analytics:online-users";
    this.socket.on(eventName, callback);
    this.listeners.set(eventName, callback);
  }

  /**
   * Listen for new signup events
   * @param {Function} callback - Called with user data
   */
  onNewSignup(callback) {
    if (!this.socket) return;

    const eventName = "analytics:new-signup";
    this.socket.on(eventName, callback);
    this.listeners.set(eventName, callback);
  }

  /**
   * Listen for new payment events
   * @param {Function} callback - Called with payment data
   */
  onNewPayment(callback) {
    if (!this.socket) return;

    const eventName = "analytics:new-payment";
    this.socket.on(eventName, callback);
    this.listeners.set(eventName, callback);
  }

  /**
   * Remove all event listeners
   */
  removeAllListeners() {
    if (!this.socket) return;

    this.listeners.forEach((callback, eventName) => {
      this.socket.off(eventName, callback);
    });
    this.listeners.clear();
  }

  /**
   * Remove specific event listener
   * @param {string} eventName - Name of the event
   */
  removeListener(eventName) {
    if (!this.socket) return;

    const callback = this.listeners.get(eventName);
    if (callback) {
      this.socket.off(eventName, callback);
      this.listeners.delete(eventName);
    }
  }

  /**
   * Check if socket is connected
   */
  isConnected() {
    return this.socket?.connected || false;
  }
}

// Export singleton instance
const socketClient = new SocketClient();
export default socketClient;
